
import { v4 as uuidv4 } from "uuid";
import { ModelBase, Indexable } from "@models";
import { CharacterLimitation, CreatureLimitation, ItemLimitation } from "@dndModels/misc";
import { BastionFacilitySize, DataType } from "@dndModels/Types";

// import DataUtilities from "../utilities/common_utilities";
// import { DataUtilitiesClass } from "../utilities/common_utilities_class";

/**
 * Bastion Facilities are facilities that you can build into your Bastion (HQ), 
 * and which you're able to do things with using Bastion Turns, 
 * which by default take 1 week of in game time, but DMs are free to adjust this.
 */

export class BastionFacility extends ModelBase {
  data_type: DataType = "bastion_facility";
  feature_source_type = "BastionFacility";
  special_facility = false;
  hirelings = 1;
  max_hirelings = 5;
  size: BastionFacilitySize = "Cramped";
  limitations: CharacterLimitation[] = [];
  subtypes: string[] = [];
  repeatable = false;
  
  capacity_for_size: { [size: string]: number } = {};
  
  enlarge_roomy_cost = 500;
  enlarge_roomy_time = 25;
  enlarge_vast_cost = 2000;
  enlarge_vast_time = 80;

  abilities: BastionAbility[] = [];
  profit_percent_potence: { [char_level: number]: number } = {};

  // get orders() {
  //   return this.feature_bases;
  // }

  get special_string() {
    return this.special_facility ? " Special" : "";
  }

  get_detail_format() {
    const str = `${super.get_detail_format()}; ${this.size}${this.special_string}`;
    return str.trim();
  }
  
  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.special_facility = obj.special_facility ? obj.special_facility : false;
      this.hirelings = obj.hirelings !== undefined ? obj.hirelings : 1;
      this.repeatable = obj.repeatable ? obj.repeatable : false;
      this.size = obj.size ? obj.size : "Cramped";
      if (obj.abilities) {
        this.abilities = obj.abilities.map((o: any) => { return new BastionAbility(o); });
      }
      this.limitations = [];
      if (obj.limitations) {
        obj.limitations.forEach((l: any) => {
          this.limitations.push(new CharacterLimitation(l));
        });
      }
      this.subtypes = obj.subtypes ? obj.subtypes : [];

      this.capacity_for_size = obj.capacity_for_size ? obj.capacity_for_size : {};
      this.enlarge_roomy_cost = obj.enlarge_roomy_cost !== undefined ? obj.enlarge_roomy_cost : 500;
      this.enlarge_roomy_time = obj.enlarge_roomy_time !== undefined ? obj.enlarge_roomy_time : 25;
      this.enlarge_vast_cost = obj.enlarge_vast_cost !== undefined ? obj.enlarge_vast_cost : 2000;
      this.enlarge_vast_time = obj.enlarge_vast_time !== undefined ? obj.enlarge_vast_time : 80;
      
      this.profit_percent_potence = {};
      if (obj.profit_percent_potence) {
        this.profit_percent_potence = obj.profit_percent_potence;
      } else if (obj.abilities) {
        const ability = obj.abilities.find((o: any) => o.items.find((o2: any) => Object.keys(o2.profit_percent_potence).length > 0));
        if (ability) {
          const item = ability.items.find((o2: any) => Object.keys(o2.profit_percent_potence).length > 0);
          if (item) {
            this.profit_percent_potence = item.profit_percent_potence;
          }
        }
      }
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.special_facility = obj.special_facility ? obj.special_facility : false;
  //     this.hirelings = obj.hirelings !== undefined ? obj.hirelings : 1;
  //     this.repeatable = obj.repeatable ? obj.repeatable : false;
  //     this.size = obj.size ? obj.size : "Cramped";
  //     if (obj.abilities) {
  //       this.abilities = obj.abilities.map((o: any) => { return new BastionAbility(o); });
  //     }
  //     this.limitations = [];
  //     if (obj.limitations) {
  //       obj.limitations.forEach((l: any) => {
  //         this.limitations.push(new CharacterLimitation(l));
  //       });
  //     }
  //     this.subtypes = obj.subtypes ? obj.subtypes : [];

  //     this.capacity_for_size = obj.capacity_for_size ? obj.capacity_for_size : {};
  //     this.enlarge_roomy_cost = obj.enlarge_roomy_cost !== undefined ? obj.enlarge_roomy_cost : 500;
  //     this.enlarge_roomy_time = obj.enlarge_roomy_time !== undefined ? obj.enlarge_roomy_time : 25;
  //     this.enlarge_vast_cost = obj.enlarge_vast_cost !== undefined ? obj.enlarge_vast_cost : 2000;
  //     this.enlarge_vast_time = obj.enlarge_vast_time !== undefined ? obj.enlarge_vast_time : 80;
      
  //     this.profit_percent_potence = {};
  //     if (obj.profit_percent_potence) {
  //       this.profit_percent_potence = obj.profit_percent_potence;
  //     } else if (obj.abilities) {
  //       const ability = obj.abilities.find((o: any) => o.items.find((o2: any) => Object.keys(o2.profit_percent_potence).length > 0));
  //       if (ability) {
  //         const item = ability.items.find((o2: any) => Object.keys(o2.profit_percent_potence).length > 0);
  //         if (item) {
  //           this.profit_percent_potence = item.profit_percent_potence;
  //         }
  //       }
  //     }
  //   }
  // }

  toDBObj(): any {
    const feature_source_obj = super.toDBObj();
    return {
      ...feature_source_obj,
      special_facility: this.special_facility,
      hirelings: this.special_facility ? this.hirelings : 0,
      repeatable: this.repeatable,
      size: this.size,
      abilities: this.abilities.map(o => o.toDBObj()),
      limitations: this.limitations.map(o => o.toDBObj()),
      subtypes: this.subtypes,
      capacity_for_size: this.capacity_for_size,
      enlarge_roomy_cost: this.enlarge_roomy_cost,
      enlarge_roomy_time: this.enlarge_roomy_time,
      enlarge_vast_cost: this.enlarge_vast_cost,
      enlarge_vast_time: this.enlarge_vast_time,
      profit_percent_potence: this.profit_percent_potence,
    };
  }

  copy(copyMe: BastionFacility, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.special_facility = copyMe.special_facility;
    this.hirelings = copyMe.hirelings;
    this.repeatable = copyMe.repeatable;
    this.size = copyMe.size;
    this.abilities = copyMe.abilities.map(o => o.clone());
    this.limitations = copyMe.limitations.map(o => o.clone());
    this.subtypes = [...copyMe.subtypes];
    this.capacity_for_size = {...copyMe.capacity_for_size};
    this.enlarge_roomy_cost = copyMe.enlarge_roomy_cost;
    this.enlarge_roomy_time = copyMe.enlarge_roomy_time;
    this.enlarge_vast_cost = copyMe.enlarge_vast_cost;
    this.enlarge_vast_time = copyMe.enlarge_vast_time;
    this.profit_percent_potence = {...copyMe.profit_percent_potence};
  }

  clone(drop_ids = false): BastionFacility {
    const new_obj = new BastionFacility();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class BastionAbility implements Indexable {
  true_id = "";
  name = "";
  description = "";
  type: "Craft" | "Harvest" | "Recruit" | "Trade" | "Research" | "Empower" | "Other" = "Craft";
  items: BastionItem[] = []; // Craft, Harvest, Recruit, and Trade reference items.
  duration = 1;
  temp_hp = 0; // Demiplane gives temp_hp on a long rest in it when empowered
  gift_ids: string[] = []; // Empower and Other give Gifts to you and/or others

  get id() {
    return this.true_id;
  }
  
  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.name = obj.name ? obj.name : "";
      this.description = obj.description ? obj.description : "";
      this.type = obj.type ? obj.type : "Crafting";
      this.items = obj.items ? obj.items.map((o: any) => new BastionItem(o)) : [];
      this.duration = obj.duration !== undefined ? obj.duration : 1;
      this.temp_hp = obj.temp_hp ? obj.temp_hp : 0;
      this.gift_ids = obj.gift_ids ? obj.gift_ids : [];
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      name: this.name,
      description: this.description,
      type: this.type,
      items: this.items.map(o => o.toDBObj()),
      duration: this.duration,
      temp_hp: this.temp_hp,
      gift_ids: this.gift_ids
    };
  }

  copy(copyMe: BastionAbility): void {
    this.true_id = copyMe.true_id;
    this.name = copyMe.name;
    this.description = copyMe.description;
    this.type = copyMe.type;
    this.items = copyMe.items.map(o => o.clone());
    this.duration = copyMe.duration;
    this.temp_hp = copyMe.temp_hp;
    this.gift_ids = [...copyMe.gift_ids];
  }

  clone(): BastionAbility {
    const new_obj = new BastionAbility();
    new_obj.copy(this);
    return new_obj;
  }
}

export type BastionCustomItem = { true_id: string, name: string, count: number, value: number };

export class BastionItem implements Indexable {
  true_id = "";
  name = "";
  description = "";
  type: "Item" | "Creature" | "Custom" = "Item";
  custom_options: BastionCustomItem[] = [];
  item_limit: ItemLimitation = new ItemLimitation(); // The kinds of items it can be used for
  creature_limit: CreatureLimitation = new CreatureLimitation(); // The kinds of creatures it can be used for
  char_limit: CharacterLimitation = new CharacterLimitation(); // The kind of char able to have it made
  count = 1;
  time_days = 1;
  cost_gold = 0;
  for_subtypes: string[] = [];

  get id() {
    return this.true_id;
  }
  
  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.name = obj.name ? obj.name : "";
      this.description = obj.description ? obj.description : "";
      this.type = obj.type ? obj.type : "Item";
      this.custom_options = []; 
      if (obj.custom_options) {
        for (const opt of obj.custom_options) {
          if (typeof opt === "string") {
            if (opt.includes(" x ")) {
              const pieces = opt.split(" x ");
              this.custom_options.push({ true_id: uuidv4().toString(), name: pieces[0], count: +pieces[1], value: 0 });
            } else {
              this.custom_options.push({ true_id: uuidv4().toString(), name: opt, count: 1, value: 0 });
            }
          } else {
            this.custom_options.push(opt);
          }
        }
      }
      this.item_limit = obj.item_limit ? new ItemLimitation(obj.item_limit) : new ItemLimitation();
      this.creature_limit = obj.creature_limit ? new CreatureLimitation(obj.creature_limit) : new CreatureLimitation();
      this.char_limit = obj.char_limit ? new CharacterLimitation(obj.char_limit) : new CharacterLimitation();
      this.count = obj.count ? obj.count : 1;
      this.time_days = obj.time_days !== undefined ? obj.time_days : 1;
      this.cost_gold = obj.cost_gold ? obj.cost_gold : 0;
      this.for_subtypes = obj.for_subtypes ? obj.for_subtypes : [];
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      name: this.name,
      description: this.description,
      type: this.type,
      custom_options: this.custom_options,
      item_limit: this.item_limit.toDBObj(),
      creature_limit: this.creature_limit.toDBObj(),
      char_limit: this.char_limit.toDBObj(),
      count: this.count,
      time_days: this.time_days,
      cost_gold: this.cost_gold,
      for_subtypes: this.for_subtypes
    };
  }

  copy(copyMe: BastionItem): void {
    this.true_id = copyMe.true_id;
    this.name = copyMe.name;
    this.description = copyMe.description;
    this.type = copyMe.type;
    this.custom_options = copyMe.custom_options.map(o => { return {...o}; });
    this.item_limit = copyMe.item_limit.clone();
    this.creature_limit = copyMe.creature_limit.clone();
    this.char_limit = copyMe.char_limit.clone();
    this.count = copyMe.count;
    this.time_days = copyMe.time_days;
    this.cost_gold = copyMe.cost_gold;
    this.for_subtypes = [...copyMe.for_subtypes];
  }

  clone(): BastionItem {
    const new_obj = new BastionItem();
    new_obj.copy(this);
    return new_obj;
  }
}
